import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Package,
  Store,
  Receipt,
  BarChart3,
  Settings,
  LogOut,
  Users,
  Ticket,
  HelpCircle,
  Tags,
  FileText,
  ShoppingCart,
  Truck,
  Image,
  Mail,
  Menu,
  X,
  DollarSign,
  Wrench,
  Building2,
  ChevronDown,
  ClipboardList,
  UserCheck,
  Wallet,
  Crown,
  TrendingUp,
  KeyRound,
  QrCode,
  ShoppingBag,
  LayoutGrid,
  Layers,
  RefreshCw,
  List,
  Clock,
  MapPin,
  Radio
} from 'lucide-react';

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isSuperAdmin = user?.role === 'superadmin';

  const superAdminItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/superadmin' },
    { icon: Store, label: 'Negocios', path: '/superadmin/negocios' },
    { icon: Image, label: 'Banners', path: '/superadmin/banners' },
    { icon: LayoutGrid, label: 'Landing', path: '/superadmin/landing-layout' },
    { icon: Ticket, label: 'Tickets Soporte', path: '/superadmin/tickets' },
    { icon: Tags, label: 'Categorías', path: '/superadmin/categorias' },
    { icon: Building2, label: 'Tipos de Negocio', path: '/superadmin/tipos-negocio' },
    { icon: Layers, label: 'Categorias Maestras', path: '/superadmin/categorias-maestras' },
    { icon: Mail, label: 'Email', path: '/superadmin/email' },
    { icon: KeyRound, label: 'Licencias POS', path: '/superadmin/licencias-pos' },
    { icon: DollarSign, label: 'Ventas Licencias', path: '/superadmin/ventas-licencias' },
    { icon: ShoppingBag, label: 'Compras Web', path: '/superadmin/compras' },
    { icon: RefreshCw, label: 'Sync POS', path: '/superadmin/sync-dashboard' },
    { icon: Radio, label: 'Delivery Hub', path: '/superadmin/delivery-hub' },
    { icon: FileText, label: 'Facturación', path: '/superadmin/facturacion' },
    { icon: Settings, label: 'Configuración', path: '/superadmin/configuracion' },
  ];

  const businessMenu = [
    { type: 'link', icon: LayoutDashboard, label: 'Resumen', path: '/dashboard' },
    {
      type: 'group', key: 'ventas', icon: DollarSign, label: 'Ventas',
      children: [
        { icon: ShoppingCart, label: 'Abrir POS', path: '/pos' },
        { icon: Receipt, label: 'Historial Ventas', path: '/dashboard/ventas' },
        { icon: ClipboardList, label: 'Cotizaciones', path: '/dashboard/cotizaciones' },
        { icon: UserCheck, label: 'Clientes', path: '/dashboard/clientes' },
      ],
    },
    { type: 'link', icon: Radio, label: 'Delivery Hub', path: '/dashboard/delivery-hub' },
    {
      type: 'group', key: 'facturacion', icon: FileText, label: 'Facturación',
      children: [
        { icon: Receipt, label: 'Facturación de Tickets', path: '/dashboard/facturacion' },
        { icon: FileText, label: 'Facturación Manual', path: '/dashboard/facturacion-manual' },
      ],
    },
    {
      type: 'group', key: 'operacion', icon: Package, label: 'Operación',
      children: [
        { icon: Package, label: 'Productos', path: '/dashboard/productos' },
        { icon: Tags, label: 'Secciones', path: '/dashboard/secciones' },
        { icon: List, label: 'Listas de Precios', path: '/dashboard/listas-precios' },
        { icon: Receipt, label: 'Inventario', path: '/dashboard/inventario' },
        { icon: Clock, label: 'Cierres de Turno', path: '/dashboard/cierres-turno' },
        { icon: RefreshCw, label: 'Sync Log', path: '/dashboard/sync-log' },
        { icon: Package, label: 'Ajustes Inventario', path: '/dashboard/ajustes-inventario' },
        { icon: Users, label: 'Personal', path: '/dashboard/cajeros' },
        { icon: Truck, label: 'Reparto', path: '/dashboard/repartidores' },
        { icon: MapPin, label: 'Seguimiento', path: '/dashboard/seguimiento-entregas' },
      ],
    },
    {
      type: 'group', key: 'estructura', icon: Building2, label: 'Estructura',
      children: [
        { icon: Store, label: 'Sucursales', path: '/dashboard/sucursales' },
        { icon: Receipt, label: 'Cajas', path: '/dashboard/cajas' },
      ],
    },
    {
      type: 'group', key: 'reportes', icon: BarChart3, label: 'Reportes',
      children: [
        { icon: TrendingUp, label: 'Analisis Ventas', path: '/dashboard/analisis-ventas' },
        { icon: DollarSign, label: 'Ventas', path: '/dashboard/reportes/ventas' },
        { icon: Package, label: 'Inventario', path: '/dashboard/reportes/inventario' },
        { icon: Wallet, label: 'Caja', path: '/dashboard/reportes/caja' },
        { icon: TrendingUp, label: 'Productos Top', path: '/dashboard/reportes/top-productos' },
        { icon: ShoppingCart, label: 'Marketplace', path: '/dashboard/reportes/marketplace' },
        { icon: ClipboardList, label: 'Cotizaciones', path: '/dashboard/reportes/cotizaciones' },
      ],
    },
    {
      type: 'group', key: 'sistema', icon: Wrench, label: 'Sistema',
      children: [
        { icon: Settings, label: 'Configuración', path: '/dashboard/configuracion' },
        { icon: QrCode, label: 'Menú Digital', path: '/dashboard/menu-digital' },
        { icon: KeyRound, label: 'Licencias POS', path: '/dashboard/licencias-pos' },
        { icon: Crown, label: 'Plan y Complementos', path: '/dashboard/plan' },
        { icon: ShoppingBag, label: 'Comprar Addons', path: '/dashboard/comprar-addons' },
        { icon: HelpCircle, label: 'Soporte', path: '/dashboard/soporte' },
      ],
    },
  ];

  // Determine which groups should be open based on current route
  const getInitialOpenGroups = () => {
    const open = [];
    businessMenu.forEach((item) => {
      if (item.type === 'group') {
        const hasActive = item.children.some(
          (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (hasActive) open.push(item.key);
      }
    });
    return open;
  };

  const [openGroups, setOpenGroups] = useState(getInitialOpenGroups);

  // Update open groups when route changes
  useEffect(() => {
    businessMenu.forEach((item) => {
      if (item.type === 'group') {
        const hasActive = item.children.some(
          (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
        );
        if (hasActive) {
          setOpenGroups((prev) => (prev.includes(item.key) ? prev : [...prev, item.key]));
        }
      }
    });
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const sidebarContent = (
    <>
      <div className="p-4 md:p-6 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Todo en Local</h1>
          <p className="text-sm text-slate-400 mt-1">{isSuperAdmin ? 'SuperAdmin' : user?.full_name}</p>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden p-1 rounded-lg hover:bg-slate-800 text-slate-400"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 p-2 md:p-4 space-y-0.5 md:space-y-1 overflow-y-auto">
        {isSuperAdmin
          ? superAdminItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-lg transition-colors text-sm md:text-base ${
                    isActive
                      ? 'bg-teal-700 text-white'
                      : 'hover:bg-slate-800 text-slate-300'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })
          : businessMenu.map((item) => {
              if (item.type === 'link') {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 rounded-lg transition-colors text-sm md:text-base ${
                      isActive
                        ? 'bg-teal-700 text-white'
                        : 'hover:bg-slate-800 text-slate-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              }

              const Icon = item.icon;
              const isOpen = openGroups.includes(item.key);
              const hasActiveChild = item.children.some(
                (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
              );

              return (
                <div key={item.key}>
                  <button
                    onClick={() => toggleGroup(item.key)}
                    className={`flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 w-full rounded-lg transition-colors text-sm md:text-base ${
                      hasActiveChild
                        ? 'text-white'
                        : 'hover:bg-slate-800 text-slate-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium flex-1 text-left">{item.label}</span>
                    <ChevronDown
                      className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      isOpen ? 'max-h-[40rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="ml-2 pl-4 border-l border-slate-700 space-y-0.5 py-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            data-testid={`nav-${child.label.toLowerCase()}`}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                              isChildActive
                                ? 'bg-teal-700 text-white'
                                : 'hover:bg-slate-800 text-slate-400'
                            }`}
                          >
                            <ChildIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
      </nav>

      {!isSuperAdmin && (
        <div className="p-3 md:p-4 border-t border-slate-800">
          <Link
            to="/pos"
            data-testid="nav-pos-btn"
            className="flex items-center justify-center gap-2 px-4 py-2.5 md:py-3 w-full bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-lg hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg shadow-teal-500/25 text-sm md:text-base"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Abrir POS</span>
          </Link>
        </div>
      )}

      <div className="p-3 md:p-4 border-t border-slate-800">
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-3 px-3 py-2.5 md:px-4 md:py-3 w-full rounded-lg hover:bg-slate-800 text-slate-300 transition-colors text-sm md:text-base"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Cerrar Sesión</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900 text-white flex items-center px-4 h-14 shadow-lg">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1.5 rounded-lg hover:bg-slate-800"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="ml-3 text-lg font-bold">Todo en Local</h1>
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop: static, mobile: overlay drawer */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-64 bg-slate-900 text-slate-300 flex flex-col
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:flex-shrink-0
        `}
      >
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 md:pt-0">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};